import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import Image from 'next/image';
import { videoExtensions } from '@/lib/uppy.util';
import ReactPlayer from 'react-player/lazy';
import Placeholder from '@/assets/images/image-placeholder.svg';

const mediaBorderRadiusBySize: { [key: string]: string } = {
  'x-small': '0.6rem',
  small: '0.6rem',
  medium: '0.8rem',
  large: '2.1rem',
  'x-large': '2.1rem',
};

type MediaSizeType =
  | 'x-small'
  | 'small'
  | 'medium'
  | 'large'
  | 'x-large'
  | `${number}rem`;

const PlayerHeightAndWidthBySize = (size: MediaSizeType): string => {
  const predefinedSizes = {
    'x-small': '2.5rem',
    small: '5rem',
    medium: '12rem',
    large: '24rem',
    'x-large': '32rem',
  };

  if (size in predefinedSizes) {
    return predefinedSizes[size as keyof typeof predefinedSizes];
  }

  return size.endsWith('rem') ? size : `${size}rem`;
};

const MediaElement: React.FC<{
  url?: string;
  className?: string;
  editable?: boolean | null;
  hideVideoControls?: boolean;
  size?: MediaSizeType;
  width?: number | string;
  height?: number | string;
}> = ({
  url,
  className,
  editable,
  hideVideoControls,
  size = 'small',
  width,
  height,
}) => {
  const isInitiallyVideo = videoExtensions.some(
    (extension) => url && url?.endsWith(extension)
  );
  const [loadError, setLoadError] = useState(false);
  const playerParams = size
    ? {
        height: PlayerHeightAndWidthBySize(size),
        width: PlayerHeightAndWidthBySize(size),
      }
    : {
        height: 'calc(100% - 2.6rem)',
        width: '100%',
      };
  const cssVideoOutline = (size: MediaSizeType) => ({
    border: size === 'small' ? 'lightgrey solid 1px' : 'none',
    borderRadius:
      size in mediaBorderRadiusBySize
        ? mediaBorderRadiusBySize[size]
        : '0.6rem',
    overflow: 'hidden',
  });

  if (loadError) {
    return (
      <Image
        className={className}
        src={Placeholder}
        alt='image-placeholder'
        width={200}
        height={200}
      />
    );
  }

  return url && isInitiallyVideo ? (
    <ReactPlayer
      onError={() => setLoadError(true)}
      url={url}
      controls={!hideVideoControls}
      {...playerParams}
      style={size && { ...cssVideoOutline(size) }}
    />
  ) : (
    <div className={cn(editable ? 'block' : 'inline-block', className)}>
      <Image
        onError={() => setLoadError(true)}
        className={className}
        src={url || ''}
        alt={url || ''}
        width={0}
        height={0}
        sizes='100vw'
        priority
        loading='eager'
        style={{ width: width || '100%', height: height || 'auto' }}
      />
    </div>
  );
};

export default MediaElement;
